import { useLanguage } from '../../contexts/useLanguage';
import { ClerkSecuredByFooter } from './ClerkSecuredByFooter';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '../ui/card';
import { BorderBeam } from '../ui/border-beam';

function ShimmerBar({ className }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-zinc-200/90 ${className ?? ''}`} aria-hidden />;
}

/**
 * Static shell while Clerk `useSignIn` / SDK is not ready — avoids a lone centered spinner.
 */
export function ClerkSignInCardSkeleton() {
  const { t } = useLanguage();

  return (
    <div
      className="pointer-events-none w-full max-w-[420px] select-none"
      role="status"
      aria-busy="true"
      aria-label={t('clerkAuth.loading')}
    >
      <Card className="relative overflow-hidden rounded-2xl border-zinc-200/90 bg-white/95 shadow-xl backdrop-blur-sm">
        <div className="relative z-10">
          <CardHeader className="pb-2">
            <ShimmerBar className="h-7 w-52 max-w-[85%]" />
            <div className="mt-3 space-y-2">
              <ShimmerBar className="h-4 w-full" />
              <ShimmerBar className="h-4 w-[92%]" />
            </div>
            <div className="pt-3">
              <ShimmerBar className="h-4 w-64" />
            </div>
          </CardHeader>
          <CardContent className="grid gap-4 pb-2">
            <div className="grid gap-2">
              <ShimmerBar className="h-4 w-16" />
              <ShimmerBar className="h-10 w-full rounded-lg" />
            </div>
            <div className="grid gap-2">
              <ShimmerBar className="h-4 w-20" />
              <ShimmerBar className="h-10 w-full rounded-lg" />
            </div>
          </CardContent>
          <CardFooter className="border-t border-zinc-100 pt-2">
            <ShimmerBar className="h-10 w-full rounded-lg" />
          </CardFooter>
        </div>
        <BorderBeam duration={8} size={100} borderWidth={2} colorFrom="#ffaa40" colorTo="#9c40ff" />
      </Card>
      <ClerkSecuredByFooter />
    </div>
  );
}

/**
 * Static shell while Clerk `useSignUp` / SDK is not ready.
 */
export function ClerkSignUpCardSkeleton() {
  const { t } = useLanguage();

  return (
    <div
      className="pointer-events-none w-full max-w-md select-none"
      role="status"
      aria-busy="true"
      aria-label={t('clerkAuth.loading')}
    >
      <div className="rounded-2xl border border-zinc-200 bg-white p-8 shadow-sm">
        <ShimmerBar className="h-7 w-48 max-w-[90%]" />
        <div className="mt-3">
          <ShimmerBar className="h-4 w-56" />
        </div>
        <div className="mt-8 space-y-4">
          <div>
            <ShimmerBar className="h-4 w-14" />
            <ShimmerBar className="mt-2 h-10 w-full rounded-lg" />
          </div>
          <div>
            <ShimmerBar className="h-4 w-20" />
            <ShimmerBar className="mt-2 h-10 w-full rounded-lg" />
          </div>
          <ShimmerBar className="h-10 w-full rounded-lg" />
        </div>
      </div>
      <ClerkSecuredByFooter />
    </div>
  );
}
